import { useMemo, useState } from "react";
import { calculateTotals } from "../Dishes/DishCalculation";
import DishPrice from "../Dishes/DishPrice";

export default function useBillCalculator(initialDishes = DishPrice) {
  // ✅ dishes must be state if you want to add custom ones
  const [dishes, setDishes] = useState(() => initialDishes);

  const [quantities, setQuantities] = useState(() =>
    Array(initialDishes.length).fill(0)
  );
  const [eachPersonBillSummary, setEachPersonBillSummary] = useState([]);

  const totals = useMemo(() => {
    return calculateTotals(quantities, dishes);
  }, [quantities, dishes]);

  const addDish = (index) => {
    setQuantities((prev) => {
      const next = [...prev];
      next[index] = (next[index] ?? 0) + 1;
      return next;
    });
  };

  const minusDish = (index) => {
    setQuantities((prev) => {
      const next = [...prev];
      next[index] = Math.max(0, (next[index] ?? 0) - 1);
      return next;
    });
  };

  const resetCalculator = () => {
    setQuantities(() => Array(dishes.length).fill(0));
    setEachPersonBillSummary([]);
  };

  const undoLastAction = () => {
    const last = eachPersonBillSummary[eachPersonBillSummary.length - 1];
    if (!last) return;

    setEachPersonBillSummary((prev) => prev.slice(0, -1));
    setQuantities(last.quantities);
  };

  const calculateNextPersonBill = () => {
    
    const totalDishesForThisPerson = quantities.reduce((sum, qty) => sum + (qty || 0), 0);

    setEachPersonBillSummary((prev) => {
      const personNumber = prev.length + 1;

      return [
        ...prev,
        {
          personName: `${personNumber}`,
          quantities: [...quantities], // ✅ snapshot
          onePersonBillSummary: totals.totalPriceWithServiceCharge,
          onePersonTotalDishes: totalDishesForThisPerson,
        },
      ];
    });

    setQuantities(() => Array(dishes.length).fill(0));
  };

  const addCustomDish = (price) => {
    const p = Number(price);
    if (!Number.isFinite(p) || p <= 0) return;

    // ✅ prevent duplicates
    setDishes((prev) => {
      if (prev.some((d) => d.price === p)) return prev;
      return [...prev, { id: p, price: p }];
    });

    // ✅ keep quantities in sync
    setQuantities((prev) => [...prev, 0]);
  };

  return {
    dishes, // ✅ return dishes so DishCalculator can render it
    quantities,
    eachPersonBillSummary,
    totals,
    actions: {
      addDish,
      minusDish,
      addCustomDish,
      resetCalculator,
      undoLastAction,
      calculateNextPersonBill,
    },
  };
}
